import React, { useState } from 'react';
import { Typography, Box, Alert, Snackbar } from '@mui/material';
import { useConnectionStatus } from '../../hooks/useConnectionStatus';
import WhatsAppCard from '../agent/WhatsAppCard';
import WhatsAppOficialCard from '../agent/WhatsAppOficialCard';
import ZAPICard from '../agent/ZAPICard';
import WordpressCard from '../agent/WordpressCard';
import WebChatCard from '../agent/WebChatCard';
import CRMIntegrationCard from '../agent/CRMIntegrationCard';
import AgendamentoCard from '../agent/AgendamentoCard';
import QRCodeDialog from '../agent/QRCodeDialog';
import WhatsAppConfigModal from './WhatsAppConfigModal';
import { WhatsAppConfig } from '../../utils/agentApi';
import { Agent } from '../../types/agent';
import { agentApi } from '../../utils/agentApi';
import { useTranslation } from 'react-i18next';

interface ConnectionsSectionProps {
  agentId: string;
  agent?: Agent | null;
  onAgentUpdate?: (agent: Agent) => void;
  onStatusChange?: () => void;
  connection?: ReturnType<typeof useConnectionStatus>;
}

const ConnectionsSection: React.FC<ConnectionsSectionProps> = ({ agentId, agent, onAgentUpdate, onStatusChange, connection }) => {
  const { t } = useTranslation();
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const localConnection = useConnectionStatus({ agentId, agent, onAgentUpdate });
  const {
    status,
    snackbarOpen,
    snackbarMessage,
    snackbarSeverity,
    setSnackbarOpen,
    connectWhatsAppNative,
    disconnectWhatsAppNative,
    connectZApi,
    disconnectZApi,
    connectWhatsAppOficial,
    disconnectWhatsAppOficial,
    whatsappStatus,
    isWhatsAppOficialActive,
  } = connection ?? localConnection;


  const handleConfigureClick = () => {
    setConfigModalOpen(true);
  };

  const handleConfigSave = async (config: WhatsAppConfig) => {
    if (!agent) return;

    setIsSavingConfig(true);
    try {
      const updatedAgent = await agentApi.updateWhatsAppConfig(agentId, config);
      if (onAgentUpdate) {
        onAgentUpdate(updatedAgent);
      }
    } finally {
      setIsSavingConfig(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>{t('agents.connections')}</Typography>

      <WhatsAppCard
        status={{
          isConnected: status.whatsappNative.isConnected,
          loggedIn: status.whatsappNative.isConnected,
          phoneNumber: status.whatsappNative.phoneNumber,
        }}
        isLoading={status.whatsappNative.isLoading}
        isCheckingStatus={status.whatsappNative.isCheckingStatus}
        onConnect={async () => { await connectWhatsAppNative(); onStatusChange?.(); }}
        onDisconnect={async () => { await disconnectWhatsAppNative(); onStatusChange?.(); }}
        onConfigure={handleConfigureClick}
        agentId={agentId}
        whatsappConnected={agent?.whatsappConnected}
        agent={agent}
        onAgentUpdate={onAgentUpdate}
        isZApiConnected={agent?.whatsappConnected !== 'oficial' ? status.zapi.isConnected : false}
        isWhatsAppOficialConnected={isWhatsAppOficialActive}
      />

      <ZAPICard
        agentId={agentId}
        agent={agent}
        onAgentUpdate={onAgentUpdate}
        isWhatsAppNativeConnected={agent?.whatsappConnected !== 'oficial' ? status.whatsappNative.isConnected : false}
        isWhatsAppOficialConnected={isWhatsAppOficialActive}
        onConnectZApi={async () => { await connectZApi(); onStatusChange?.(); }}
        onDisconnectZApi={async () => { await disconnectZApi(); onStatusChange?.(); }}
      />

      <WhatsAppOficialCard
        agentId={agentId}
        agent={agent}
        onSuccess={async () => {await connectWhatsAppOficial(); onStatusChange?.(); }}
        onAgentUpdate={onAgentUpdate}
        status={status.whatsappOficial}
        isWhatsAppNativeConnected={agent?.whatsappConnected !== 'oficial' ? status.whatsappNative.isConnected : false}
        isZApiConnected={agent?.whatsappConnected !== 'oficial' ? status.zapi.isConnected : false}
        onDisconnect={async () => {await disconnectWhatsAppOficial(); onStatusChange?.(); }}
      />

      {agent && (
        <AgendamentoCard
          agent={agent}
          onAgentUpdate={onAgentUpdate}
        />
      )}

      {agent && (
        <CRMIntegrationCard
          agent={agent}
          onAgentUpdate={onAgentUpdate}
        />
      )}

      {agent && (
        <WebChatCard agent={agent} />
      )}

      <WordpressCard />

      <QRCodeDialog
        open={whatsappStatus.isDialogOpen}
        onClose={whatsappStatus.closeQRDialog}
        isLoading={whatsappStatus.isLoading}
        qrCodeImage={whatsappStatus.qrCodeImage}
        qrCodeError={whatsappStatus.qrCodeError}
        onGenerateNewQRCode={whatsappStatus.generateNewQRCode}
      />

      <WhatsAppConfigModal
        open={configModalOpen}
        onClose={() => setConfigModalOpen(false)}
        agent={agent || null}
        onSave={handleConfigSave}
        isLoading={isSavingConfig}
      />

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConnectionsSection;


